import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserRound, Plus, Loader2, Pencil, Trash2, Mail, Phone, Building2 } from 'lucide-react';
import { useOwners, useSaveOwner, useDeleteOwner, type Owner } from '../../../hooks/useManagement';
import { useConfirm } from '../../../components/ConfirmDialog';
import { useToast } from '../../../components/ToastProvider';
import { useDirtyForm } from '../../../hooks/useDirtyForm';
import SaveButton from '../../../components/SaveButton';

interface Draft { id?: number; name: string; email: string; phone: string; notes: string }
const EMPTY: Draft = { name: '', email: '', phone: '', notes: '' };

export default function ManageOwnersPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const toast = useToast();
  const { data: owners, isLoading } = useOwners();
  const save = useSaveOwner();
  const del = useDeleteOwner();
  const [form, setForm] = useState<Draft | null>(null);
  const { isDirty, resetBaseline } = useDirtyForm(form);

  const openForm = (draft: Draft) => { setForm(draft); resetBaseline(draft); };

  const doSave = async () => {
    if (!form || !form.name.trim()) return;
    try {
      await save.mutateAsync(form);
      toast.success(form.id ? t('portal.manage.owners.updated', 'Propietario actualizado.') : t('portal.manage.owners.created', 'Propietario creado.'));
      setForm(null);
    } catch { toast.error(t('portal.manage.owners.errSave', 'No se ha podido guardar.')); }
  };

  const remove = async (o: Owner) => {
    if ((await confirm({
      title: t('portal.manage.owners.deleteTitle', 'Eliminar propietario'),
      message: o.property_count > 0
        ? t('portal.manage.owners.deleteWithProps', '"{{name}}" tiene {{n}} alojamiento(s) asignados. Quedarán sin propietario.', { name: o.name, n: o.property_count })
        : o.name,
      confirmLabel: t('portal.manage.ses.deleteYes', 'Eliminar'), tone: 'danger',
    })) !== 'confirm') return;
    await del.mutateAsync(o.id);
    toast.success(t('portal.manage.owners.deleted', 'Propietario eliminado.'));
  };

  return (
    <div className="w-full space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><UserRound size={22} className="text-teal-600" /> {t('portal.manage.owners.title', 'Propietarios')}</h1>
          <p className="text-sm text-slate-500 mt-1">{t('portal.manage.owners.subtitle', 'Los propietarios de los alojamientos que gestionas. Asigna cada alojamiento a un propietario y genera sus informes.')}</p>
        </div>
        {!form && (
          <button onClick={() => openForm({ ...EMPTY })} className="inline-flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm font-semibold">
            <Plus size={16} /> {t('portal.manage.owners.add', 'Nuevo propietario')}
          </button>
        )}
      </div>

      {form && (
        <form onSubmit={e => { e.preventDefault(); void doSave(); }} className="rounded-2xl border border-slate-200 bg-white p-5 grid sm:grid-cols-2 gap-4">
          <Field label={t('portal.manage.owners.name', 'Nombre *')}><input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
          <Field label={t('portal.manage.owners.email', 'Email')}><input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputCls} /></Field>
          <Field label={t('portal.manage.owners.phone', 'Teléfono')}><input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputCls} /></Field>
          <Field label={t('portal.manage.owners.notes', 'Notas')}><input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className={inputCls} /></Field>
          <div className="sm:col-span-2 flex items-center gap-2">
            <SaveButton onClick={doSave} saving={save.isPending} isDirty={isDirty && !!form.name.trim()} label={t('portal.manage.owners.save', 'Guardar')} savedLabel={t('common.saved', 'Guardado')} size="sm" />
            <button type="button" onClick={() => setForm(null)} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900">{t('portal.manage.owners.cancel', 'Cancelar')}</button>
          </div>
        </form>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center py-16"><Loader2 className="animate-spin text-teal-500" size={28} /></div>
        ) : !(owners ?? []).length ? (
          <div className="py-14 text-center text-sm text-slate-400 px-6"><UserRound size={32} className="mx-auto mb-2 text-slate-300" />{t('portal.manage.owners.empty', 'Aún no has añadido propietarios.')}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="px-5 py-3">{t('portal.manage.owners.name', 'Nombre')}</th>
                <th className="px-5 py-3">{t('portal.manage.owners.contact', 'Contacto')}</th>
                <th className="px-5 py-3 text-right">{t('portal.manage.owners.properties', 'Alojamientos')}</th>
                <th className="px-5 py-3"></th>
              </tr></thead>
              <tbody className="divide-y divide-slate-100">
                {(owners ?? []).map(o => (
                  <tr key={o.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-900">{o.name}</div>
                      {o.notes && <div className="text-xs text-slate-400 truncate max-w-[16rem]">{o.notes}</div>}
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500">
                      {o.email && <div className="flex items-center gap-1"><Mail size={11} />{o.email}</div>}
                      {o.phone && <div className="flex items-center gap-1"><Phone size={11} />{o.phone}</div>}
                      {!o.email && !o.phone && '—'}
                    </td>
                    <td className="px-5 py-3 text-right"><span className="inline-flex items-center gap-1 text-slate-600"><Building2 size={13} /> {o.property_count}</span></td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button onClick={() => openForm({ id: o.id, name: o.name, email: o.email ?? '', phone: o.phone ?? '', notes: o.notes ?? '' })} className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md"><Pencil size={14} /></button>
                      <button onClick={() => remove(o)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-md"><Trash2 size={14} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

const inputCls = 'w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500';
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label className="block"><span className="block text-xs font-medium text-slate-600 mb-1">{label}</span>{children}</label>;
}
